import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { API_BASE_URL } from '../services/api';
import { getFriendlyFormName } from './parentPacketFriendlyNames';
import { HASC_BLUE } from './woodmerePacketBrand';

export default function PhysicianFormsDownload({
  token,
  siteSlug,
  physicianForms = [],
  /** 'default' | 'hascBlue' — Woodmere packet chrome uses outline HASC blue. */
  variant = 'default',
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const isHasc = variant === 'hascBlue';

  const handleDownload = useCallback(async () => {
    if (!token || downloading) return;
    setError('');
    setDownloading(true);
    try {
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/physician-forms/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf, application/json;q=0.9, */*;q=0.8',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Unable to download doctor forms right now.');
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] || 'HASC_Doctor_Forms.pdf';
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      setError(e?.message || 'Unable to download doctor forms right now.');
    } finally {
      setDownloading(false);
    }
  }, [token, downloading]);

  if (!physicianForms?.length) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        p: { xs: 1.5, md: 2 },
        bgcolor: isHasc ? HASC_BLUE.highlight : '#f0f7ff',
        borderColor: isHasc ? HASC_BLUE.primary : 'primary.light',
        borderWidth: isHasc ? 1.5 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <LocalHospitalIcon
          sx={{
            mt: 0.25,
            color: isHasc ? HASC_BLUE.primary : undefined,
          }}
          color={isHasc ? undefined : 'primary'}
          aria-hidden="true"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
            Download forms for your doctor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Print this PDF bundle and bring it to your child&apos;s pediatrician. Each page includes a
            QR code so returned forms can be matched to your child.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
            <strong>Includes:</strong>
            {' '}
            {physicianForms.map((f) => getFriendlyFormName(siteSlug, f.form_id, f.form_name)).join(' · ')}
          </Typography>
          {!!error && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          <Button
            variant={isHasc ? 'outlined' : 'contained'}
            color={isHasc ? undefined : 'primary'}
            onClick={handleDownload}
            disabled={downloading}
            fullWidth={isHasc}
            sx={isHasc ? {
              borderColor: HASC_BLUE.primary,
              color: HASC_BLUE.primary,
              fontWeight: 600,
              py: { xs: 1.1, md: 0.75 },
              '&:hover': {
                borderColor: HASC_BLUE.primaryHover,
                bgcolor: '#fff',
                color: HASC_BLUE.primaryHover,
              },
            } : undefined}
          >
            {downloading ? 'Preparing PDF…' : 'Download forms for your doctor'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
